import React, { useState } from 'react';
import { MapPin, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { saveRelistingOwners } from '../../../api/dashboard';
import { CRM_ENTERED } from './RelistingOwnerContacts';

/**
 * The Edit form on a Re-listing row (Christian Gravias, DOMA 13382334362,
 * 26 Oct 2026): "Add this form, only make this edit button available to
 * Internal media & tech team. Not real estate agents. I want to use this edit
 * feature for our database team to get data".
 *
 * Name, phone and email for up to two owners, laid out like Add Lead. It edits
 * only the owners entered here; the spreadsheet ones keep showing beside them
 * and are replaced by their own re-import. The address is the listing's and is
 * shown read-only: it is what ties these owners to the row.
 */

const BLANK = { name: '', phone: '', email: '' };
const INPUT = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400';
const LABEL = 'block text-xs font-semibold text-gray-600 mb-1';

const fromContact = (c) => ({
  name: c.contact_name || c.owner_name || '',
  phone: (c.phones || []).join(', '),
  email: c.email || '',
});

export default function RelistingOwnerEditModal({ row, contacts, onClose, onSaved }) {
  const entered = (contacts || []).filter((c) => c.sheet === CRM_ENTERED).slice(0, 2).map(fromContact);
  const [owners, setOwners] = useState(entered.length ? entered : [{ ...BLANK }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (i, field, value) => {
    setError('');
    setOwners((prev) => prev.map((o, j) => (j === i ? { ...o, [field]: value } : o)));
  };
  const filled = (o) => Boolean(o.name.trim() || o.phone.trim() || o.email.trim());

  const save = async (e) => {
    e.preventDefault();
    const send = owners.filter(filled);
    if (send.some((o) => !o.name.trim())) {
      setError('Each owner needs a name.');
      return;
    }
    setSaving(true);
    try {
      const res = await saveRelistingOwners(row.campaign_id, send);
      onSaved(res?.data?.owner_contacts || []);
      toast.success(send.length ? 'Owner details saved.' : 'Owner details removed.');
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save the owner details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onMouseDown={onClose}
    >
      <form
        onSubmit={save}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Edit owner details"
        className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900">Edit owner details</h3>
            <p className="text-xs text-gray-500">Shown on this re-listing opportunity for the agents.</p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="p-1 rounded-md text-gray-400 hover:bg-gray-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {owners.map((o, i) => (
            <div key={i} className="space-y-3">
              {i > 0 && (
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs font-bold text-gray-700">Second owner</span>
                  <button
                    type="button"
                    onClick={() => setOwners((prev) => prev.filter((_, j) => j !== i))}
                    className="text-xs font-semibold text-gray-400 hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              )}
              <div>
                <label className={LABEL} htmlFor={`owner-name-${i}`}>Name *</label>
                <input id={`owner-name-${i}`} className={INPUT} value={o.name} onChange={(e) => set(i, 'name', e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL} htmlFor={`owner-phone-${i}`}>Phone</label>
                  <input id={`owner-phone-${i}`} type="tel" className={INPUT} value={o.phone} onChange={(e) => set(i, 'phone', e.target.value)} />
                </div>
                <div>
                  <label className={LABEL} htmlFor={`owner-email-${i}`}>Email</label>
                  <input id={`owner-email-${i}`} type="email" className={INPUT} value={o.email} onChange={(e) => set(i, 'email', e.target.value)} />
                </div>
              </div>
            </div>
          ))}
          {owners.length < 2 && (
            <button
              type="button"
              onClick={() => setOwners((prev) => [...prev, { ...BLANK }])}
              className="inline-flex items-center gap-1 text-xs font-semibold text-teal-600 hover:underline"
            >
              <Plus className="w-3.5 h-3.5" /> Add a second owner
            </button>
          )}
          <div>
            <span className={LABEL}>Property address</span>
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="truncate">{row.address}</span>
            </div>
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-60">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
